// node_modules
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Components
import { FindestButton, FindestEditableSpan, FindestTextBox, FiveStars } from "Components";
import { TechnologyListItemVotes } from "Components/Case/Results/ConceptTechnologyList/TechnologyListItem/TechnologyListItemVotes";
import { EditableTechnologyRequirementScore } from "Components/Case/Results/TechnologyRequirements/EditableTechnologyRequirementScore";
// Constants
import { ConceptConstants } from "Constants";
// Controllers
import { RequirementControllerSingleton } from "Controllers";
// Enums
import { CaseActionTypeEnum, CasePageEnum, VoteTypeEnum, ToastTypeEnum } from "Enums";
// Custom hooks
import { useDebounce } from "Hooks";
// Contexts
import { CaseContext, AuthContext } from "Stores";
// Types
import { TTiOverviewDTO, TRequirementDTO } from "Types";
// Styles
import styles from "./requirementsTable.module.scss";
// Interfaces
import { ICaseSetSelectedEntity } from "Interfaces";
// Helpers
import { ToastHelperSingleton } from "Helpers";

type TRequirementsTableProps = {
    tiOverviewDto: TTiOverviewDTO | null,
    voteType: VoteTypeEnum
}

export const RequirementsTable: FC<TRequirementsTableProps> = ({ tiOverviewDto, voteType }: TRequirementsTableProps) => {
    // State
    const [isCreatingColumn, setIsCreatingColumn] = useState(false);
    const [newRequirementName, setNewRequirementName] = useState<string | undefined>(undefined);
    const [currentRequirements, setCurrentRequirements] = useState<TRequirementDTO[]>([]);
    const [editedRequirement, setEditedRequirement] = useState<string | undefined>(undefined);
    const [editedRequirementId, setEditedRequirementId] = useState<number | undefined>(undefined);

    // Let
    const debouncedEditedRequirement = useDebounce(editedRequirement, 500);

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    // Logic
    const changeRequirementName = useCallback(async (requirementId: number, name: string) => {
        if (!requirementId || !name || !debouncedEditedRequirement || !editedRequirement || !editedRequirementId) return;
        setEditedRequirement(undefined);
        setEditedRequirementId(undefined);
        const isSuccess = await RequirementControllerSingleton.changeRequirement(requirementId, name, true);
        if (!isSuccess) ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Could not change requirement name.");
    }, [debouncedEditedRequirement, editedRequirement, editedRequirementId]);

    useEffect(() => {
        if (!debouncedEditedRequirement || !editedRequirementId) return;
        changeRequirementName(editedRequirementId, debouncedEditedRequirement);
    }, [changeRequirementName, debouncedEditedRequirement, editedRequirementId]);

    useEffect(() => {
        if (!tiOverviewDto) return;
        if (tiOverviewDto.requirements) setCurrentRequirements(tiOverviewDto.requirements);
    }, [tiOverviewDto]);

    useEffect(() => {
        if(!caseState.conceptTechnologyList) return;
        if(!caseState.conceptTechnologyList.publishedPhaseOptions.doShowRequirementsTable) {
            caseDispatch({
                type: CaseActionTypeEnum.SetCaseSelectedEntity, selectedEntityId: -1,
                selectedEntityName: CasePageEnum.Overview, selectedEntityNumbering: ""
            } as ICaseSetSelectedEntity);
        }
    }, [caseState.conceptTechnologyList, caseDispatch]);

    const onChangeRequirementName = (text: string) => {
        setNewRequirementName(text);
    };

    const addRequirementColumn = () => {
        setIsCreatingColumn(!isCreatingColumn);
    };

    const saveRequirementColumn = async () => {
        if (!newRequirementName || newRequirementName.trim().length === 0 || !caseState.selectedCaseId) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Please enter a valid requirement name");
            return;
        }
        setIsCreatingColumn(false);

        const requirement: TRequirementDTO = {
            id: -1,
            caseId: caseState.selectedCaseId,
            name: newRequirementName,
            type: 1,
            isUserAdded: true
        };
        const newRequirement = await RequirementControllerSingleton.addRequirement(caseState.selectedCaseId, requirement, true);
        setCurrentRequirements(currentRequirements.concat(newRequirement));
        setNewRequirementName(undefined);
    };

    return (
        <>
            <div className={styles.tableHeaderContainer}>
                <h2>Requirements table</h2>
                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ? <FindestButton title="+ Add a new column" onClick={addRequirementColumn} extraClassName={styles.addNewColumnButton} /> : null}
            </div>
            <div className={[styles.tableContainer, styles.scrollbarCustomThin].join(" ")}>
                <table>
                    <colgroup>
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={styles.technologyHeaderColumn}>Result</th>
                            <th className={styles.technologyRatingColumn}>Vote</th>
                            {currentRequirements.map((requirement) => {
                                if (requirement.isUserAdded) {
                                    return (
                                        <th key={requirement.id}>
                                            {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                                <div className={styles.editableRequirementTitleContainer}>
                                                    <FindestEditableSpan 
                                                        entityId={requirement.id}
                                                        isEditableRequirementTitle={true} 
                                                        extraClassName={styles.editableRequirementTitle} 
                                                        text={requirement.name} 
                                                        onChangeText={(text: string) => { setEditedRequirementId(requirement.id); setEditedRequirement(text); }} 
                                                        resourceName={`requirement${requirement.id.toString()}`}/>
                                                </div>
                                                : 
                                                <span>
                                                    {requirement.name}
                                                </span>
                                            }
                                        </th>
                                    );
                                } else {
                                    return (
                                        <th key={requirement.id}>{requirement.name}</th>
                                    );
                                }
                            })}
                            {isCreatingColumn && <th className={styles.technologyRatingColumn}>
                                <FindestTextBox doAutoFocus={true} onChange={onChangeRequirementName} width={200} />
                                <span onClick={saveRequirementColumn}><FontAwesomeIcon icon={faSave} /></span>
                            </th>}
                        </tr>
                    </thead>
                    <tbody>
                        {caseState.conceptTechnologyList?.concepts.map((concept) => {
                            return (
                                <React.Fragment key={concept.id}>
                                    {concept.technologies.map((technology) => {
                                        let rateCount = technology.rates?.currentUserRate ? 1 : 0;
                                        if (technology.rates?.userRates) rateCount += technology.rates.userRates.length;

                                        return (
                                            <tr key={technology.id}>
                                                <td className={styles.tableTechnologyColumn}>{technology.numbering ? technology.numbering : ""} {technology.name}</td>
                                                { voteType === VoteTypeEnum.FiveStars ?
                                                    <td className={styles.tableRatingColumn}><FiveStars averageRating={technology.rates?.averageRate} count={rateCount} /></td>
                                                    : null}
                                                { voteType === VoteTypeEnum.YesNoSuper ?
                                                    <td className={styles.tableRatingColumn}>
                                                        <TechnologyListItemVotes isOutsideSidebar={true} technology={technology}/>
                                                    </td>
                                                    : null}
                                                {currentRequirements.map((requirement) => {
                                                    let currentEditableScore = undefined;
                                                    const requirementTechnologyIndex = `${requirement.id  },${  technology.id}`;
                                                    if (tiOverviewDto?.requirementScores) {
                                                        for (let i = 0; i < tiOverviewDto.requirementScores.length; i++) {
                                                            // TODO: optimize this
                                                            const currentScore = tiOverviewDto.requirementScores[i];
                                                            if (currentScore.technologyId === technology.id && currentScore.requirementId === requirement.id) {
                                                                currentEditableScore = currentScore;
                                                                break;
                                                            }
                                                        }
                                                    }

                                                    return (
                                                        <td className={styles.tableRequirementColumn} key={requirementTechnologyIndex}>
                                                            <EditableTechnologyRequirementScore
                                                                showRequirementName={false}
                                                                requirement={requirement}
                                                                score={currentEditableScore}
                                                                isRequirementTable={true} 
                                                                technologyId={technology.id}
                                                                originalTechnologyId={technology.originalTechnologyId ? technology.originalTechnologyId : technology.id} />
                                                        </td>
                                                    );
                                                })}
                                            </tr>);
                                    })}
                                </React.Fragment>);
                        })}
                        {caseState.conceptTechnologyList && caseState.conceptTechnologyList.additionalTechnologies.length > 0 ?
                            <React.Fragment key={ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID}>
                                {caseState.conceptTechnologyList.additionalTechnologies.map((technology) => {
                                    let rateCount = technology.rates?.currentUserRate ? 1 : 0;
                                    if (technology.rates?.userRates) rateCount += technology.rates.userRates.length;

                                    return (
                                        <tr key={technology.id}>
                                            <td className={styles.tableTechnologyColumn}>{technology.numbering ? technology.numbering : ""} {technology.name}</td>
                                            { voteType === VoteTypeEnum.FiveStars ?
                                                    <td className={styles.tableRatingColumn}><FiveStars averageRating={technology.rates?.averageRate} count={rateCount} /></td>
                                                    : null}
                                            { voteType === VoteTypeEnum.YesNoSuper ?
                                                <td className={styles.tableRatingColumn}>
                                                    <TechnologyListItemVotes isOutsideSidebar={true} technology={technology}/>
                                                </td>
                                                : null}
                                            {currentRequirements.map((requirement) => {
                                                let currentEditableScore = undefined;
                                                const requirementTechnologyIndex = `${requirement.id  },${  technology.id}`;
                                                if (tiOverviewDto && tiOverviewDto.requirementScores) {
                                                    for (let i = 0; i < tiOverviewDto.requirementScores.length; i++) {
                                                        // TODO: optimize this
                                                        const currentScore = tiOverviewDto.requirementScores[i];
                                                        if (currentScore.technologyId === technology.id && currentScore.requirementId === requirement.id) {
                                                            currentEditableScore = currentScore;
                                                            break;
                                                        }
                                                    }
                                                }

                                                return (
                                                    <td className={styles.tableRequirementColumn} key={requirementTechnologyIndex}>
                                                        <EditableTechnologyRequirementScore
                                                            showRequirementName={false}
                                                            requirement={requirement}
                                                            score={currentEditableScore}
                                                            isRequirementTable={true} 
                                                            technologyId={technology.id}
                                                            originalTechnologyId={technology.originalTechnologyId ? technology.originalTechnologyId : technology.id} />
                                                    </td>
                                                );
                                            })}
                                        </tr>);
                                })}
                            </React.Fragment>
                        : null}
                    </tbody>
                </table>
            </div>
        </>
    );
};